import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Card,
	CardContent,
	Container,
	Stack,
	TextField,
	Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { MapContainer, Marker, Popup, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/axios";
import DefaultInput from "../../components/form/DefaultInput";
import DefaultPasswordInput from "../../components/form/DefaultPasswordInput";
import AddressSelect from "../../components/form/AddressSelect";

interface OrderHistory {
	title: string;
	subTitle: string;
	description: string;
	statusId: number;
}

interface TrackOrderDetailsResponse {
	buyerNameAndSurname?: string;
	sellerNameAndSurname?: string;
	buyerAddress?: string;
	buyerPhoneNumber?: string;
	latitude?: string;
	longitude?: string;
	cityId?: number | string;
	cityLabel?: string;
	countryLabel?: string;
	country?: string;
	trackingLink?: string;
	shortOrderHistory: OrderHistory[];
}

const DEFAULT_CENTER: [number, number] = [42.6629, 21.1655];

const parseCoordinate = (value?: string) => {
	if (!value) {
		return null;
	}

	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

const OrderTracking = () => {
	const [params] = useSearchParams();
	const [lookupValue, setLookupValue] = useState("");
	const [trackingCode, setTrackingCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [orderDetails, setOrderDetails] =
		useState<TrackOrderDetailsResponse | null>(null);
	const [requestedDate, setRequestedDate] = useState<string>("");
	const [requestedAddress, setRequestedAddress] = useState("");
	const [driverInstructions, setDriverInstructions] = useState("");

	const [countryLabel, setCountryLabel] = useState("xk");
	const [cityLabel, setCityLabel] = useState<string | number | undefined>();

	const [hasUnlockedOrder, setHasUnlockedOrder] = useState(false);
	const [verifiedOrderIdentifier, setVerifiedOrderIdentifier] = useState("");

	// const mapPosition = useMemo<[number, number]>(() => DEFAULT_CENTER, []);
	const mapPosition = useMemo<[number, number]>(() => {
		const latitude = parseCoordinate(orderDetails?.latitude);
		const longitude = parseCoordinate(orderDetails?.longitude);

		if (latitude === null || longitude === null) {
			return DEFAULT_CENTER;
		}

		return [latitude, longitude];
	}, [orderDetails?.latitude, orderDetails?.longitude]);
	const mapKey = `${mapPosition[0]}-${mapPosition[1]}`;

	const loadOrderData = useCallback(async (value: string, code: string) => {
		if (!value) {
			toast.error("Mungon ID-ja e gjurmimit.");
			return;
		}

		if (!code) {
			toast.error("Ju lutem shkruani kodin e gjurmimit nga mesazhi.");
			return;
		}
		const body = {
			orderIdentifier: value,
			code: code,
		};

		setLoading(true);
		try {
			const detailsResponse = await api.post(
				"OrderTracking/tracking-order",
				body
			);

			const details = detailsResponse.data as TrackOrderDetailsResponse;
			setOrderDetails({
				buyerNameAndSurname: details?.buyerNameAndSurname,
				sellerNameAndSurname: details?.sellerNameAndSurname,
				buyerAddress: details?.buyerAddress,
				buyerPhoneNumber: details?.buyerPhoneNumber,
				latitude: details?.latitude,
				longitude: details?.longitude,
				trackingLink: details?.trackingLink,
				shortOrderHistory: Array.isArray(details?.shortOrderHistory)
					? details.shortOrderHistory
					: [],
			});
			setRequestedAddress(details?.buyerAddress ?? "");
			setCityLabel(details?.cityId ?? details?.cityLabel);
			setVerifiedOrderIdentifier(value);
			setHasUnlockedOrder(true);
		} catch (error) {
			setOrderDetails(null);
			setHasUnlockedOrder(false);
			setVerifiedOrderIdentifier("");
			setCityLabel(undefined);
			toast.error("Kodi ose ID-ja e gjurmimit nuk është e saktë.");
		} finally {
			setLoading(false);
		}
	}, []);

	const cleanState = () => {
		setDriverInstructions("");
		setRequestedAddress("");
		setRequestedDate("");
	};
	useEffect(() => {
		const fromQuery =
			params.get("orderId") ??
			params.get("displayId") ??
			params.get("id") ??
			"";
		if (fromQuery) {
			setLookupValue(fromQuery);
		}
	}, [params]);

	const submitBuyerRequest = async () => {
		if (!verifiedOrderIdentifier) {
			toast.error("Së pari zhbllokoni porosinë.");
			return;
		}

		const payload = {
			orderId: lookupValue,
			code: trackingCode,
			deliveryDate: requestedDate || null,
			address: requestedAddress,
			deliveryInstructions: driverInstructions,
		};

		try {
			await api.post("OrderTracking/tracking-order-update", payload);
			toast.success("Kërkesa juaj për përditësim u dërgua.");
			cleanState();
		} catch (error) {
			toast.error("Kërkesa nuk u dërgua. Ju lutem provoni përsëri.");
		}
	};

	return (
		<Container
			maxWidth="lg"
			sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 2 } }}
		>
			<Stack spacing={{ xs: 2, md: 3 }}>
				<Card variant="outlined">
					<CardContent sx={{ p: { xs: 2, sm: 3 } }}>
						<Typography
							variant="h5"
							sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
						>
							Gjurmimi i porosisë
						</Typography>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mt: 0.75, mb: 2 }}
						>
							Shkruani kodin e gjurmimit që ju është dërguar për
							të zhbllokuar detajet e porosisë.
						</Typography>
						<Stack spacing={1.25}>
							<div className="flex flex-wrap items-center gap-4">
								<DefaultInput
									label="ID e porosisë ose ID e shfaqjes"
									value={lookupValue}
									onChange={(event) =>
										setLookupValue(event.target.value)
									}
									className="registerInput"
								/>
								<DefaultPasswordInput
									label="Kodi i gjurmimit"
									value={trackingCode}
									onChange={(event) =>
										setTrackingCode(event.target.value)
									}
									className="registerInput"
									labelClass={"mb-0"}
								/>
							</div>
							<Button
								className="registerButton"
								onClick={() =>
									loadOrderData(
										lookupValue.trim(),
										trackingCode.trim()
									)
								}
								disabled={loading}
								sx={{
									width: { xs: "100%", sm: "auto" },
									minWidth: { sm: 220 },
								}}
							>
								{loading
									? "Duke verifikuar..."
									: "Shiko detajet e gjurmimit"}
							</Button>
						</Stack>
					</CardContent>
				</Card>

				{!hasUnlockedOrder && (
					<Alert severity="info">
						Detajet e porosisë do të shfaqen pasi të verifikohet
						kodi i gjurmimit.
					</Alert>
				)}

				{hasUnlockedOrder && orderDetails && (
					<>
						<Grid container spacing={{ xs: 1.5, md: 2 }}>
							<Grid item xs={12} md={5}>
								<Card
									variant="outlined"
									sx={{ height: "100%" }}
								>
									<CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
										<Typography
											variant="h6"
											sx={{
												fontSize: {
													xs: "1rem",
													sm: "1.15rem",
												},
											}}
										>
											Përmbledhja e porosisë
										</Typography>
										<Stack spacing={1} mt={1}>
											<Typography variant="body2">
												<strong>Blerësi:</strong>{" "}
												{orderDetails.buyerNameAndSurname ||
													"-"}
											</Typography>
											<Typography variant="body2">
												<strong>Shitësi:</strong>{" "}
												{orderDetails.sellerNameAndSurname ||
													"-"}
											</Typography>
											<Typography variant="body2">
												<strong>Telefoni:</strong>{" "}
												{orderDetails.buyerPhoneNumber ||
													"-"}
											</Typography>
											<Typography variant="body2">
												<strong>Adresa:</strong>{" "}
												{orderDetails.buyerAddress ||
													"Not available"}
											</Typography>

											{orderDetails?.trackingLink && (
												<Typography variant="body2">
													<strong>
														Gjurmimi i shoferit:
													</strong>{" "}
													<a
														href={
															orderDetails?.trackingLink
														}
														target="_blank"
														rel="noreferrer"
														style={{
															display:
																"inline-flex",
															alignItems:
																"center",
															gap: 6,
														}}
													>
														<span aria-hidden="true">
															👉
														</span>
														Kliko këtu për te parë
														ku është shoferi në kohë
														reale
													</a>
												</Typography>
											)}
										</Stack>
									</CardContent>
								</Card>
							</Grid>
							<Grid item xs={12} md={7}>
								<Card
									variant="outlined"
									sx={{ height: "100%" }}
								>
									<CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
										<Typography
											variant="h6"
											sx={{
												fontSize: {
													xs: "1rem",
													sm: "1.15rem",
												},
												mb: 1,
											}}
										>
											Harta e dorëzimit
										</Typography>
										<Box
											sx={{
												height: {
													xs: 220,
													sm: 280,
													md: 320,
												},
												borderRadius: 2,
												overflow: "hidden",
											}}
										>
											<MapContainer
												key={mapKey}
												center={mapPosition}
												zoom={13}
												scrollWheelZoom={false}
												style={{
													width: "100%",
													height: "100%",
												}}
											>
												<TileLayer
													attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
													url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
												/>
												<Marker position={mapPosition}>
													<Popup>
														{orderDetails.buyerAddress ||
															"Lokacioni i dorëzimit"}
													</Popup>
												</Marker>
											</MapContainer>
										</Box>
									</CardContent>
								</Card>
							</Grid>
						</Grid>

						<Grid container spacing={{ xs: 1.5, md: 2 }}>
							<Grid item xs={12} md={7}>
								<Card
									variant="outlined"
									sx={{ height: "100%" }}
								>
									<CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
										<Typography
											variant="h6"
											sx={{
												fontSize: {
													xs: "1rem",
													sm: "1.15rem",
												},
												mb: 1.5,
											}}
										>
											Historiku i porosisë
										</Typography>
										{orderDetails.shortOrderHistory
											.length === 0 ? (
											<Alert severity="info">
												Nuk u gjet asnjë hyrje historiku
												për këtë porosi.
											</Alert>
										) : (
											<Stack spacing={1.25}>
												{orderDetails.shortOrderHistory.map(
													(item, index) => {
														const isLast =
															index ===
															orderDetails
																.shortOrderHistory
																.length -
																1;
														return (
															<Box
																key={`${item.statusId}-${item.title}-${index}`}
																sx={{
																	position:
																		"relative",
																	pl: 3.5,
																	pb: isLast
																		? 0
																		: 1.5,
																}}
															>
																<Box
																	sx={{
																		position:
																			"absolute",
																		left: 8,
																		top: 8,
																		width: 10,
																		height: 10,
																		borderRadius:
																			"50%",
																		bgcolor:
																			"primary.main",
																	}}
																/>
																{!isLast && (
																	<Box
																		sx={{
																			position:
																				"absolute",
																			left: 12,
																			top: 20,
																			bottom: -6,
																			width: 2,
																			bgcolor:
																				"divider",
																		}}
																	/>
																)}
																<Typography variant="subtitle2">
																	{item.title}
																</Typography>
																<Typography
																	variant="caption"
																	color="text.secondary"
																>
																	{
																		item.subTitle
																	}
																</Typography>
																{item.description && (
																	<Typography
																		variant="body2"
																		sx={{
																			mt: 0.4,
																		}}
																	>
																		{
																			item.description
																		}
																	</Typography>
																)}
															</Box>
														);
													}
												)}
											</Stack>
										)}
									</CardContent>
								</Card>
							</Grid>
							<Grid item xs={12} md={5}>
								<Card variant="outlined">
									<CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
										<Typography
											variant="h6"
											sx={{
												fontSize: {
													xs: "1rem",
													sm: "1.15rem",
												},
												mb: 1.5,
											}}
										>
											Kërko ndryshim të dorëzimit
										</Typography>
										<Stack spacing={1.25}>
											<TextField
												size="small"
												label="Data e kërkuar e dorëzimit"
												type="date"
												fullWidth
												InputLabelProps={{
													shrink: true,
												}}
												value={requestedDate}
												onChange={(event) =>
													setRequestedDate(
														event.target.value
													)
												}
											/>
											<div className="flex flex-col items-start ">
												<label className="w-42 -mt-1">
													Adresa e përditësuar
												</label>
												<AddressSelect
													onAddressChange={(
														location
													) =>
														setRequestedAddress(
															location.label ?? ""
														)
													}
													country={countryLabel}
													selectedAddress={
														requestedAddress
													}
													city={cityLabel}
												/>
											</div>
											<DefaultInput
												label="Udhëzime për shoferin"
												value={driverInstructions}
												onChange={(event) =>
													setDriverInstructions(
														event.target.value
													)
												}
												className="registerInput"
											/>

											<Button
												onClick={submitBuyerRequest}
												sx={{
													width: {
														xs: "100%",
														sm: "auto",
													},
												}}
												className="registerButton"
											>
												Dërgo kërkesën
											</Button>
										</Stack>
									</CardContent>
								</Card>
							</Grid>
						</Grid>
					</>
				)}
			</Stack>
		</Container>
	);
};

export default OrderTracking;
